import { Button } from "@resume-studio/ui";
import { cn } from "@resume-studio/utils";
import { ColumnDef } from "@tanstack/react-table";
import { Megaphone, Pause, Pencil, Play, PlusCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";

import { AdminCampaignRow, useAdminCampaigns, useSetCampaignEnabled } from "@/admin/services";

import { AdminDataTable } from "../../components/ui/data-table";
import { EmptyState } from "../_components/empty-state";
import { PageHeader } from "../_components/page-header";

const STATUS_CLASSES: Record<AdminCampaignRow["status"], string> = {
  active: "bg-success-soft text-success-foreground",
  scheduled: "bg-primary-soft text-primary",
  ended: "bg-surface text-muted-foreground",
  paused: "bg-warning-soft text-warning-foreground",
};

const StatusBadge = ({ status }: { status: AdminCampaignRow["status"] }) => (
  <span
    className={cn(
      "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em]",
      STATUS_CLASSES[status],
    )}
  >
    {status}
  </span>
);

type StatusFilter = AdminCampaignRow["status"] | "all";

export const AdminCampaignsPage = () => {
  const { data, isLoading } = useAdminCampaigns();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const columns = useMemo<ColumnDef<AdminCampaignRow>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Campaign",
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium text-foreground">{row.original.name}</span>
            <span className="mono text-[11px] text-subtle">/{row.original.slug}</span>
          </div>
        ),
      },
      {
        accessorKey: "offerType",
        header: "Offer",
        cell: ({ row }) =>
          row.original.offerType === "STRIPE_DISCOUNT" ? (
            <span className="text-[12px]">
              <span className="font-semibold tabular-nums">
                {row.original.discountPercent ?? 0}% off
              </span>{" "}
              <span className="text-subtle">·</span>{" "}
              <span className="text-muted-foreground tabular-nums">
                {row.original.discountDurationMonths ?? 0}mo
              </span>
            </span>
          ) : (
            <span className="text-[12px]">
              <span className="font-semibold uppercase tracking-[0.04em]">
                {row.original.grantedPlan}
              </span>{" "}
              <span className="text-subtle">·</span>{" "}
              <span className="text-muted-foreground tabular-nums">
                {row.original.grantedDurationDays}d
              </span>
            </span>
          ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "funnel",
        header: "Funnel",
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5 text-[12px] tabular-nums">
            <span title="Views" className="text-subtle">
              {row.original.metrics.views.toLocaleString()}
            </span>
            <span className="text-subtle/60">›</span>
            <span title="Claims" className="text-muted-foreground">
              {row.original.metrics.claims.toLocaleString()}
            </span>
            <span className="text-subtle/60">›</span>
            <span title="Conversions" className="font-semibold text-foreground">
              {row.original.metrics.conversions.toLocaleString()}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "currentRedemptions",
        header: "Redeemed",
        cell: ({ row }) => (
          <span className="text-[12px] tabular-nums text-foreground">
            {row.original.currentRedemptions.toLocaleString()}
            {row.original.maxRedemptions ? (
              <span className="text-muted-foreground"> / {row.original.maxRedemptions}</span>
            ) : null}
          </span>
        ),
      },
      {
        accessorKey: "endsAt",
        header: "Ends",
        cell: ({ row }) => (
          <span className="text-[12px] tabular-nums text-muted-foreground">
            {new Date(row.original.endsAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => <ActionsCell row={row.original} />,
      },
    ],
    [],
  );

  // Pre-aggregate counts so the filter pills can show a "(N)" hint.
  const counts = useMemo(() => {
    const seed = { all: data?.length ?? 0, active: 0, scheduled: 0, paused: 0, ended: 0 };
    return (data ?? []).reduce(
      (acc, row) => {
        acc[row.status] = (acc[row.status] ?? 0) + 1;
        return acc;
      },
      seed as Record<string, number>,
    );
  }, [data]);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return data ?? [];
    return (data ?? []).filter((row) => row.status === statusFilter);
  }, [data, statusFilter]);

  return (
    <>
      <Helmet>
        <title>Campaigns · Admin</title>
      </Helmet>

      <PageHeader
        title="Campaigns"
        description="Launch, pause, and measure every public offer."
        trail={["Admin"]}
        actions={
          <Button size="sm" asChild>
            <Link to="/campaigns/new" className="gap-1.5">
              <PlusCircle className="size-3.5" />
              New campaign
            </Link>
          </Button>
        }
      />

      {!isLoading && (data?.length ?? 0) === 0 ? (
        <EmptyState
          icon={Megaphone}
          title="No campaigns yet"
          description="Create your first one to start tracking the funnel."
          action={
            <Button asChild size="sm">
              <Link to="/campaigns/new">Create campaign</Link>
            </Button>
          }
        />
      ) : (
        <AdminDataTable
          columns={columns}
          data={filtered}
          loading={isLoading}
          searchColumn="name"
          searchPlaceholder="Filter by name…"
          filters={{
            value: statusFilter,
            onChange: (v) => setStatusFilter(v as StatusFilter),
            options: [
              { value: "all", label: "All", count: counts.all },
              { value: "active", label: "Active", count: counts.active },
              { value: "scheduled", label: "Scheduled", count: counts.scheduled },
              { value: "paused", label: "Paused", count: counts.paused },
              { value: "ended", label: "Ended", count: counts.ended },
            ],
          }}
          onRowClick={(row) => navigate(`/campaigns/${row.id}`)}
        />
      )}
    </>
  );
};

const ActionsCell = ({ row }: { row: AdminCampaignRow }) => {
  const setEnabled = useSetCampaignEnabled(row.id);
  const navigate = useNavigate();
  const isPaused = row.status === "paused";
  const Icon = isPaused ? Play : Pause;
  const label = isPaused ? "Resume" : "Pause";
  return (
    <div className="flex items-center justify-end gap-1">
      <Button
        variant="ghost"
        size="sm"
        className="h-7 gap-1 px-2 text-xs"
        onClick={(event) => {
          event.stopPropagation();
          navigate(`/campaigns/${row.id}/edit`);
        }}
        title="Edit campaign"
      >
        <Pencil className="size-3" />
        <span className="hidden sm:inline">Edit</span>
      </Button>
      {/* Pause/resume isn't meaningful on already-ended campaigns. */}
      {row.status !== "ended" && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          onClick={(event) => {
            event.stopPropagation();
            setEnabled.mutate(isPaused);
          }}
          disabled={setEnabled.isPending}
        >
          <Icon className="size-3" />
          <span className="hidden sm:inline">{label}</span>
        </Button>
      )}
    </div>
  );
};

export default AdminCampaignsPage;
